import type { BarberScheduleResponse } from '@/api/booking/getBookingScheduleQuery/types';
import type {
  GroupScheduleDayResponse,
  GroupScheduleResponse,
} from '@/api/booking/getGroupScheduleQuery/types';

/**
 * Transform the v2 group schedule response to the format expected by the UI.
 * The v2 response is grouped by day with barberTimes array, but we need to
 * transform it to per-guest schedule arrays.
 *
 * IMPORTANT: We use index-based matching instead of ID-based matching because
 * the server returns new IDs on every cart upsert. With keepPreviousData,
 * the old response (with old IDs) might be shown while the cart has new IDs.
 * Using indices ensures stable matching since appointment order is preserved.
 *
 * @param groupSchedule - API response grouped by day
 * @param guestCount - Number of guests (appointments) to map by index
 */
export const transformGroupScheduleToGuestSchedules = (
  groupSchedule: GroupScheduleResponse,
  guestCount: number,
): Map<number, BarberScheduleResponse[]> => {
  // Use index-based map instead of ID-based to handle ID changes during cart mutations
  const guestScheduleMap = new Map<number, BarberScheduleResponse[]>();

  // Initialize empty arrays for each guest by index
  for (let i = 0; i < guestCount; i++) {
    guestScheduleMap.set(i, []);
  }

  // Transform the grouped response to per-guest schedules
  // barberTimes order matches the order of appointments sent in the request
  groupSchedule.forEach((daySchedule: GroupScheduleDayResponse) => {
    daySchedule.barberTimes.forEach((barberTime, index) => {
      const existingSchedules = guestScheduleMap.get(index) || [];

      // Transform GroupScheduleTimeSlot to ScheduleTimeSlot format
      // Keep both available and selected properties for proper filtering
      // (matching WoW behavior: filter on available || selected)
      const transformTimeSlots = (
        slots: Array<{
          time: string;
          available: boolean;
          selected: boolean;
          barberIds?: string[];
        }>,
      ) =>
        slots.map((slot) => ({
          time: slot.time,
          available: slot.available,
          // Keep selected property for filtering in ChooseTimeSlotGrid
          selected: slot.selected,
          barberIds: slot.barberIds,
        }));

      existingSchedules.push({
        day: daySchedule.day,
        availability: barberTime.availability,
        suggestedTimes: transformTimeSlots(barberTime.suggestedTimes),
        times: {
          morning: transformTimeSlots(barberTime.times.morning),
          afternoon: transformTimeSlots(barberTime.times.afternoon),
          evening: transformTimeSlots(barberTime.times.evening),
        },
      });

      guestScheduleMap.set(index, existingSchedules);
    });
  });

  return guestScheduleMap;
};
